import { ArrowLeft, Camera, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/occurrences/button'
import {
	ClassificationHelp,
	Field,
	Notice,
	Panel,
	PhotoPlaceholder,
} from '@/components/occurrences/occurrence-ui'
import { PotentialPyramid } from '@/components/occurrences/potential-pyramid'
import { PrototypeBar } from '@/components/occurrences/prototype-bar'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import {
	BODY_PARTS,
	BRANCHES,
	CLASSIFICATIONS,
	CLASSIFICATIONS_BY_TYPE,
	CONTRACTS,
	PEOPLE,
	PERSONA,
	SERVICE_TYPES,
	WORK_TYPES,
} from '@/mocks/occurrences/catalog'
import { can } from '@/mocks/occurrences/rules'
import { newId, useOccurrenceMock } from '@/mocks/occurrences/store'
import type {
	ClassificationCode,
	Evidence,
	OccurrenceType,
	Potential,
} from '@/types/occurrence'
import { TYPE_LABEL, TYPES_WITH_PERSON } from '@/types/occurrence'
import { AccessDenied } from './access-denied'

const TONES = [
	'from-slate-600 to-amber-600',
	'from-zinc-500 to-sky-400',
	'from-stone-600 to-orange-400',
]

export const OccurrenceNewPage = () => {
	const navigate = useNavigate()
	const { profile, create } = useOccurrenceMock()
	const author = PERSONA[profile].name

	const [type, setType] = useState<OccurrenceType>('WORK_ACCIDENT')
	const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
	const [time, setTime] = useState('')
	const [branch, setBranch] = useState('')
	const [contract, setContract] = useState('')
	const [badge, setBadge] = useState('')
	const [classification, setClassification] = useState<ClassificationCode | ''>(
		'',
	)
	const [bodyPart, setBodyPart] = useState('NA')
	const [potential, setPotential] = useState<Potential>('N5')
	const [workType, setWorkType] = useState('Manutenção')
	const [serviceType, setServiceType] = useState('Acessos')
	const [description, setDescription] = useState('')
	const [photos, setPhotos] = useState<Evidence[]>([])
	const [touched, setTouched] = useState(false)

	if (!can(profile, 'create'))
		return <AccessDenied message="Seu perfil não pode criar ocorrências." />

	const hasPerson = TYPES_WITH_PERSON.includes(type)
	const person = PEOPLE.find((p) => p.badge === badge)
	const future = date > new Date().toISOString().slice(0, 10)
	const errors = {
		date: future ? 'A data não pode ser futura' : '',
		branch: branch ? '' : 'Obrigatório',
		description: description.trim() ? '' : 'Obrigatório',
		person: hasPerson && !person ? 'Obrigatório' : '',
	}
	const valid = !Object.values(errors).some(Boolean)

	const save = (register: boolean) => {
		setTouched(true)
		if (register && !valid) return
		const id = create({
			type,
			status: register ? 'REGISTERED' : 'DRAFT',
			date,
			time,
			branch,
			contract,
			customer: contract ? contract.split(' - ')[0] : '',
			description,
			fullDescription: description,
			potential,
			proposedClassification: classification || null,
			bodyPart,
			workType,
			serviceType,
			employee: person
				? {
						name: person.name,
						badge: person.badge,
						jobRole: person.jobRole,
						admission: person.admission,
						age: person.age,
						education: person.education,
						pda: '',
					}
				: null,
			evidences: photos,
			statusHistory: register
				? [
						{
							at: new Date().toISOString().slice(0, 16),
							by: author,
							from: 'DRAFT',
							to: 'REGISTERED',
						},
					]
				: [],
		})
		navigate(`/occurrences/${id}`)
	}

	const showErr = (msg: string) =>
		touched && msg ? <p className="text-xs text-kaefer-red">{msg}</p> : null

	return (
		<div className="flex flex-1 flex-col">
			<PrototypeBar />
			<div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-5 sm:px-6">
				<div className="flex items-start gap-3">
					<Button
						variant="ghost"
						size="icon"
						className="mt-1 size-9"
						onClick={() => navigate(ROUTES.OCCURRENCES.INDEX)}
					>
						<ArrowLeft className="size-5" />
					</Button>
					<div>
						<h1 className="text-2xl font-semibold text-kaefer-dark-grey sm:text-3xl">
							Nova ocorrência
						</h1>
						<p className="mt-1 text-sm text-muted-foreground">
							Cadastro rápido. O tipo escolhido ativa as demais etapas; você
							completa o restante na página da ocorrência.
						</p>
					</div>
				</div>

				<Panel title="Identificação">
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<Field
							label="Tipo de ocorrência"
							required
							className="lg:col-span-2"
						>
							<NativeSelect
								value={type}
								onChange={(e) => {
									setType(e.target.value as OccurrenceType)
									setClassification('')
								}}
							>
								{(Object.keys(TYPE_LABEL) as OccurrenceType[]).map((t) => (
									<NativeSelectOption key={t} value={t}>
										{TYPE_LABEL[t]}
									</NativeSelectOption>
								))}
							</NativeSelect>
						</Field>
						<Field label="Data" required>
							<Input
								type="date"
								value={date}
								max={new Date().toISOString().slice(0, 10)}
								onChange={(e) => setDate(e.target.value)}
							/>
							{showErr(errors.date)}
						</Field>
						<Field label="Hora">
							<Input
								type="time"
								value={time}
								onChange={(e) => setTime(e.target.value)}
							/>
						</Field>
						<Field
							label="Filial"
							required
							hint="Limitada ao seu escopo de acesso"
						>
							<NativeSelect
								value={branch}
								onChange={(e) => {
									setBranch(e.target.value)
									setContract('')
								}}
							>
								<NativeSelectOption value="">Selecione</NativeSelectOption>
								{BRANCHES.map((b) => (
									<NativeSelectOption key={b} value={b}>
										{b}
									</NativeSelectOption>
								))}
							</NativeSelect>
							{showErr(errors.branch)}
						</Field>
						<Field label="Contrato" className="lg:col-span-2">
							<NativeSelect
								value={contract}
								disabled={!branch}
								onChange={(e) => setContract(e.target.value)}
							>
								<NativeSelectOption value="">
									{branch ? 'Selecione' : 'Escolha a filial primeiro'}
								</NativeSelectOption>
								{(CONTRACTS[branch] ?? []).map((c) => (
									<NativeSelectOption key={c} value={c}>
										{c}
									</NativeSelectOption>
								))}
							</NativeSelect>
						</Field>
						<Field label="Classificação proposta">
							<div className="flex items-center gap-1">
								<NativeSelect
									value={classification}
									onChange={(e) =>
										setClassification(e.target.value as ClassificationCode | '')
									}
								>
									<NativeSelectOption value="">A definir</NativeSelectOption>
									{CLASSIFICATIONS_BY_TYPE[type].map((c) => (
										<NativeSelectOption key={c} value={c}>
											{CLASSIFICATIONS[c].label}
										</NativeSelectOption>
									))}
								</NativeSelect>
								{classification && <ClassificationHelp code={classification} />}
							</div>
							<p className="text-xs text-muted-foreground">
								Será confirmada pelo Gestor SSMA.
							</p>
						</Field>
					</div>
				</Panel>

				{hasPerson && (
					<Panel
						title="Colaborador envolvido"
						description="Dados vindos da base sincronizada com o Protheus"
					>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
							<Field label="Colaborador" required className="sm:col-span-2">
								<NativeSelect
									value={badge}
									onChange={(e) => setBadge(e.target.value)}
								>
									<NativeSelectOption value="">
										Localizar colaborador (matrícula ou nome)
									</NativeSelectOption>
									{PEOPLE.map((p) => (
										<NativeSelectOption key={p.badge} value={p.badge}>
											{p.badge} — {p.name}
										</NativeSelectOption>
									))}
								</NativeSelect>
								{showErr(errors.person)}
							</Field>
							<Field label="Função">
								<Input
									readOnly
									value={person?.jobRole ?? ''}
									placeholder="Preenchida automaticamente"
								/>
							</Field>
						</div>
					</Panel>
				)}

				<Panel title="Potencial e lesão">
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:content-start">
							<Field label="Parte do corpo">
								<NativeSelect
									value={bodyPart}
									onChange={(e) => setBodyPart(e.target.value)}
								>
									{BODY_PARTS.map((b) => (
										<NativeSelectOption key={b} value={b}>
											{b}
										</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
							<Field label="Tipo da obra">
								<NativeSelect
									value={workType}
									onChange={(e) => setWorkType(e.target.value)}
								>
									{WORK_TYPES.map((b) => (
										<NativeSelectOption key={b} value={b}>
											{b}
										</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
							<Field label="Tipo do serviço">
								<NativeSelect
									value={serviceType}
									onChange={(e) => setServiceType(e.target.value)}
								>
									{SERVICE_TYPES.map((b) => (
										<NativeSelectOption key={b} value={b}>
											{b}
										</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
							<Field label="Descrição breve" required className="sm:col-span-3">
								<Textarea
									rows={4}
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="O que aconteceu, onde e com quem (sem dados médicos)"
								/>
								{showErr(errors.description)}
							</Field>
						</div>
						<PotentialPyramid value={potential} onChange={setPotential} />
					</div>
				</Panel>

				<Panel
					title="Anexos"
					description="Fotos podem ser capturadas pelo celular e são comprimidas antes do envio"
				>
					<div className="flex flex-wrap gap-3">
						{photos.map((p) => (
							<div key={p.id} className="relative">
								<PhotoPlaceholder
									tone={p.tone}
									className="h-20 w-28 rounded-lg"
									label={p.caption}
								/>
								<button
									type="button"
									aria-label="Remover foto"
									className="absolute -top-2 -right-2 rounded-full bg-kaefer-dark-grey p-1 text-white"
									onClick={() =>
										setPhotos((s) => s.filter((x) => x.id !== p.id))
									}
								>
									<X className="size-3" />
								</button>
							</div>
						))}
						<Button
							variant="outline"
							className="h-20 w-28 flex-col gap-1 border-dashed"
							onClick={() =>
								setPhotos((s) => [
									...s,
									{
										id: newId(),
										caption: `Foto ${s.length + 1}`,
										author,
										date,
										origin: 'Celular',
										confidentiality: 'INTERNAL',
										tone: TONES[s.length % TONES.length],
									},
								])
							}
						>
							<Camera className="size-5" />
							<span className="text-xs">Anexar foto</span>
						</Button>
					</div>
				</Panel>

				{touched && !valid && (
					<Notice tone="warn">
						Preencha os campos obrigatórios para registrar. Você pode salvar
						como rascunho sem validação.
					</Notice>
				)}

				<div className="sticky bottom-0 -mx-4 flex flex-wrap justify-end gap-2 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
					<Button
						variant="cancel"
						onClick={() => navigate(ROUTES.OCCURRENCES.INDEX)}
					>
						Cancelar
					</Button>
					<Button variant="outline" onClick={() => save(false)}>
						Salvar rascunho
					</Button>
					<Button
						variant="primary"
						className="font-semibold"
						onClick={() => save(true)}
					>
						Registrar ocorrência
					</Button>
				</div>
			</div>
		</div>
	)
}
